import type {
  Bulle,
  ChampsALaNaissance,
  ChangementDHabit,
  ChangementDeFiche,
  ChangementDHabitDUneBulle,
  ChangementDeMode,
  ChangementDeLigne,
  ChangementDePapier,
  EnvoiDUneLigne,
  EnvoiDUnPapier,
  ReponsePapier,
  ReponsePlaceGardee,
  ReponsePapiers,
  EcranDuTuto,
  EnvoiDeCreation,
  ErreurApi,
  GesteDuTuto,
  Etat,
  Feuille,
  Inventaire,
  Liens,
  RefusApi,
  ReponseEnvoi,
  ReponseInscription,
  ReponseSession,
  CourageDeMaintenant,
  JourneeDuJour,
  MoisDuCalendrier,
  ReponseAuBandeau,
  Ou,
  PreparationDuJour,
  PropositionDeJournee,
  ReglagesDeProposition,
  Vivier,
  VueDeCarte,
  Priorite,
  ReponseAgenda,
  ReponseBulle,
  ReponseChargement,
  ReponseContact,
  ReponseCleRevoquee,
  ReponseClesDuCompte,
  ReponseConnexionCoupee,
  ReponseConnexionsDuCompte,
  ReponseCreation,
  ReponseDossiers,
  ReponseDuBandeau,
  ReponseDuTuto,
  ReponseEmportees,
  ReponseImportDuPlan,
  ReponseInsertion,
  ReponseInviteDuPlan,
  ReponseJourneeEcrite,
  ReponseLien,
  ReponseLienChange,
  ReponseProjets,
  ReponseRangement,
  ReponseRangs,
  ReponseRecherche,
  ReponseRemplacement,
  ReponseSessionCoupee,
  ReponseSessionsDuCompte,
  ReponseSuppression,
  ReponseDemandeDAutorisation,
  ReponseDecision,
  ReponseTemplates,
  TacheEnvoyee,
  ChampsDuProfil,
  ChangementDeLigneDeProfil,
  EnvoiDUneLigneDeProfil,
  ReponseChampsDuProfil,
  ReponseFormulaireVu,
  ReponseLigneDeProfil,
  ReponseProfil,
  ReponseBoite,
  ReponseNote,
  Regroupement,
  ReponseGroupes,
  EnvoiDAbonnement,
  ReglagesDesNotifications,
  ReponseAbonnement,
  ReponseAppareilVu,
  ReponseCleDesNotifications,
  ReponseDesabonnement,
  ReponseEssai,
} from './types';
import { fuseauDuNavigateur } from '../datesEnMots';
import { enTetesDuContexte } from '../contexteDesGestes';
import type { SignalDeLecture } from '../lecturesDeLApp';
import { EN_TETES_DU_CONTEXTE, type SorteDeLien } from '@nextilt/commun';

/**
 * Le SEUL endroit du front qui fait des requêtes HTTP (CARTE.md, « Architecture » ;
 * AGENTS.md, « Couche d'accès unique »).
 * Aucun composant React n'appelle `fetch`.
 */

export class EchecApi extends Error {
  constructor(
    readonly code: string,
    readonly statut: number,
    readonly details?: unknown
  ) {
    super(code);
    this.name = 'EchecApi';
  }
}

/**
 * Les écritures parties vers le serveur et pas encore revenues. La fenêtre de bureau attend
 * qu'il n'y en ait plus avant de fermer, sinon la dernière saisie se perd (ticket 23).
 */
let ecrituresEnCours = 0;

/** Se résout quand plus aucune écriture n'est en route. L'appelant fixe le délai maximal. */
export async function attendreLesEcritures(): Promise<void> {
  while (ecrituresEnCours > 0) await new Promise((resoudre) => setTimeout(resoudre, 50));
}

async function appeler<T>(chemin: string, options: RequestInit = {}): Promise<T> {
  const ecriture = (options.method ?? 'GET') !== 'GET';
  if (!ecriture) return envoyer<T>(chemin, options);
  ecrituresEnCours += 1;
  try {
    return await envoyer<T>(chemin, options);
  } finally {
    ecrituresEnCours -= 1;
  }
}

/**
 * Le fuseau du navigateur (« Europe/Paris »), envoyé à chaque requête : « aujourd'hui », la
 * veille et le lendemain d'une journée se comptent chez l'utilisateur, pas dans le fuseau du
 * serveur (ticket 16 de retours-testeurs).
 */
function fuseau(): Record<string, string> {
  const nom = fuseauDuNavigateur();
  return nom ? { 'x-fuseau': nom } : {};
}

/**
 * Chaque requête porte le fuseau, et le contexte du geste (chantier journal-des-gestes, ticket 05) :
 * l'appareil, l'angle affiché, le courage depuis Maintenant (`contexteDesGestes.ts`). `enTetes` :
 * ce qu'un appel précise en plus (l'angle d'un signal de lecture).
 */
async function envoyer<T>(chemin: string, { headers: enTetes, ...options }: RequestInit): Promise<T> {
  let reponse: Response;
  try {
    reponse = await fetch(chemin, {
      credentials: 'same-origin',
      headers: {
        ...fuseau(),
        ...enTetesDuContexte(),
        ...(options.body ? { 'content-type': 'application/json' } : {}),
        ...(enTetes as Record<string, string> | undefined),
      },
      ...options,
    });
  } catch {
    // Réseau absent. C'est la seule erreur que l'app doit savoir raconter calmement.
    throw new EchecApi('hors_ligne', 0);
  }

  if (reponse.status === 204) return undefined as T;

  const corps = (await reponse.json().catch(() => null)) as (ErreurApi & RefusApi & T) | null;

  if (!reponse.ok) {
    throw new EchecApi(corps?.erreur ?? 'erreur_serveur', reponse.status, corps?.details);
  }

  // Un refus attendu (une boucle, une coche changée depuis) arrive en 200 pour ne pas
  // écrire de ligne rouge dans la console. Pour l'écran, il reste un geste qui n'a pas eu
  // lieu : il se lit comme un échec, avec son code et ce qu'il apprend.
  if (corps && typeof corps.refus === 'string') {
    throw new EchecApi(corps.refus, reponse.status, corps.details);
  }

  return corps as T;
}

export const api = {
  session: () => appeler<ReponseSession>('/api/session'),

  connexion: (email: string, motDePasse: string) =>
    appeler<ReponseSession>('/api/connexion', {
      method: 'POST',
      body: JSON.stringify({ email, motDePasse }),
    }),

  /**
   * Crée le compte avec le code d'invitation (chantier comptes, ticket 03). La session ne s'ouvre
   * qu'avec le lien du mail (chantier mails) : la réponse est « Regarde ta boîte mail ».
   */
  inscription: (email: string, motDePasse: string, code: string) =>
    appeler<ReponseInscription>('/api/inscription', {
      method: 'POST',
      body: JSON.stringify({ email, motDePasse, code }),
    }),

  /** Le lien du mail de vérification : ouvre la session (chantier mails). */
  verifierAdresse: (jeton: string) =>
    appeler<ReponseSession>('/api/verifier-adresse', { method: 'POST', body: JSON.stringify({ jeton }) }),

  /** Un lien neuf : par l'adresse tapée, ou, sans adresse, par la session (le bandeau). */
  renvoyerVerification: (email?: string) =>
    appeler<ReponseEnvoi>('/api/renvoyer-verification', {
      method: 'POST',
      body: JSON.stringify(email === undefined ? {} : { email }),
    }),

  motDePasseOublie: (email: string) =>
    appeler<ReponseEnvoi>('/api/mot-de-passe-oublie', { method: 'POST', body: JSON.stringify({ email }) }),

  /** Le lien du mail « mot de passe oublié » : les autres sessions coupées, celle-ci ouverte. */
  nouveauMotDePasse: (jeton: string, motDePasse: string) =>
    appeler<ReponseSession>('/api/nouveau-mot-de-passe', {
      method: 'POST',
      body: JSON.stringify({ jeton, motDePasse }),
    }),

  deconnexion: () => appeler<ReponseSession>('/api/deconnexion', { method: 'POST' }),

  /*
   * Le compte de la session, dans Paramètres (chantier parametres-du-compte) : la session du
   * navigateur seule les ouvre, jamais une clé d'accès.
   */

  /** Les sessions en cours du compte ; `ici` marque celle de cet appareil. */
  sessionsDuCompte: () => appeler<ReponseSessionsDuCompte>('/api/compte/sessions'),

  /** Couper une session par son numéro ; une session d'un autre compte répond comme une inconnue. */
  couperUneSession: (numero: string) =>
    appeler<ReponseSessionCoupee>(`/api/compte/sessions/${encodeURIComponent(numero)}`, { method: 'DELETE' }),

  /** Les clés d'accès du compte, révoquées comprises. */
  clesDuCompte: () => appeler<ReponseClesDuCompte>('/api/compte/cles'),

  revoquerUneCle: (nom: string) =>
    appeler<ReponseCleRevoquee>('/api/compte/cles/revoquer', { method: 'POST', body: JSON.stringify({ nom }) }),

  /** Les connexions de Claude au compte (chantier connecteur-claude, ticket 04). */
  connexionsDeClaude: () => appeler<ReponseConnexionsDuCompte>('/api/compte/connexions'),

  /** Couper une connexion ; une connexion d'un autre compte répond comme une inconnue. */
  couperUneConnexion: (id: string) =>
    appeler<ReponseConnexionCoupee>(`/api/compte/connexions/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /** Tout le compte en un fichier JSON : un lien qui télécharge, comme l'export `.ics`. */
  urlExportDuCompte: '/api/compte/export',

  /**
   * « Supprimer mon compte » : un mail part, son lien ouvre la page `/supprimer-mon-compte`. Le
   * premier et le dernier compte sont refusés (`EchecApi` `suppression_refusee`, la raison dans
   * `details`, lue par `comptes/suppression.ts`).
   */
  demanderLaSuppression: () => appeler<ReponseEnvoi>('/api/compte/supprimer/demander', { method: 'POST' }),

  /** Le dernier bouton de la page du lien : le lien seul suffit, sans session. */
  supprimerLeCompte: (jeton: string) =>
    appeler<ReponseSuppression>('/api/compte/supprimer', { method: 'POST', body: JSON.stringify({ jeton }) }),

  /**
   * La page Autoriser du connecteur de claude.ai (chantier connecteur-claude, ticket 02) : la
   * demande de Claude, lue par son identifiant opaque (dans l'adresse de la page), puis la décision.
   * Une demande périmée ou déjà décidée : `EchecApi` `demande_inconnue`.
   */
  demandeDAutorisation: (demande: string) =>
    appeler<ReponseDemandeDAutorisation>(`/api/oauth/demandes/${encodeURIComponent(demande)}`),
  deciderLaDemande: (demande: string, autoriser: boolean) =>
    appeler<ReponseDecision>(`/api/oauth/demandes/${encodeURIComponent(demande)}/decision`, {
      method: 'POST',
      body: JSON.stringify({ autoriser }),
    }),

  /** L'adresse de contact des pages publiques, lisible sans session (ticket 05 du chantier comptes). */
  contact: () => appeler<ReponseContact>('/api/contact'),

  /**
   * « Maintenant » (chantier flottaison) : Prioritaire, Pendant ce temps, le débloquant, À
   * vérifier. `requete` : « J'ai », « Je suis », « Courage », le projet et « Voir le reste »,
   * mis en forme par `requeteDesReglages` (`maintenant/reglagesDeMaintenant.ts`).
   */
  maintenant: (requete: string) => appeler<Vivier>(`/api/maintenant${requete ? `?${requete}` : ''}`),

  /** Les dossiers : les bulles sans parent. Il n'y a pas de table « projet ». */
  dossiers: () => appeler<ReponseDossiers>('/api/dossiers'),

  feuille: (id: string) => appeler<Feuille>(`/api/feuille/${id}`),

  /**
   * Les places de la carte avec des étapes ouvertes sur place, dans l'ordre d'ouverture
   * (ticket 06 de retours-testeurs). Le serveur calcule, le client dessine.
   */
  carte: (
    id: string,
    ouvertes: readonly string[],
    /**
     * La profondeur affichée, toujours (chantier deplacer-les-bulles, ticket 03) : le serveur y lit
     * les places gardées. L'importance mise en avant (chantier `flottaison-suite`, ticket 08) : à
     * partir de la profondeur 2, le serveur place les bulles concernées plus grandes.
     */
    profondeur: number,
    importance: Priorite | null = null
  ) =>
    appeler<VueDeCarte>(
      `/api/feuille/${id}/carte?ouvertes=${ouvertes.map(encodeURIComponent).join(',')}&profondeur=${profondeur}` +
        (importance ? `&importance=${importance}` : '')
    ),

  /**
   * La place gardée d'une bulle glissée sur la carte, cadenas ouvert (chantier deplacer-les-bulles,
   * ticket 01) : son décalage par rapport au centre de sa mère, en entiers, à cette profondeur.
   */
  placerSurLaCarte: (id: string, profondeur: number, decalage: { dx: number; dy: number }) =>
    appeler<ReponsePlaceGardee>(`/api/noeuds/${id}/place/${profondeur}`, {
      method: 'PUT',
      body: JSON.stringify(decalage),
    }),

  /** « Remettre à sa place » : la place gardée de cette profondeur s'efface, le serveur la replace. */
  remettreASaPlace: (id: string, profondeur: number) =>
    appeler<void>(`/api/noeuds/${id}/place/${profondeur}`, { method: 'DELETE' }),

  /**
   * Regrouper, en Analyse (chantier analyse-au-serveur) : les groupes par `par`, sur les bulles que
   * la carte montre avec ces étapes ouvertes sur place. Le serveur compte, l'écran dit les mots.
   */
  groupes: (id: string, par: Regroupement, ouvertes: readonly string[]) =>
    appeler<ReponseGroupes>(`/api/feuille/${id}/groupes?par=${par}&ouvertes=${ouvertes.map(encodeURIComponent).join(',')}`),

  /** La contemplation : la forme, X sur Y, la date au plus tot, et ce qu'il se passe. */
  projets: () => appeler<ReponseProjets>('/api/projets'),

  /**
   * La chronologie du monde, tous dossiers confondus. Son sujet, ce sont les collisions.
   * `vide` : aucune date nulle part, l'Agenda montre sa page de présentation (ticket 15).
   * Avec une portée (l'angle Agenda d'une feuille, chantier angles-du-projet, ticket 03) : la
   * bulle et ce qu'elle contient, filtrés par le serveur, sans journée préparée.
   */
  agenda: (portee?: string) =>
    appeler<ReponseAgenda>(`/api/agenda${portee ? `?projet=${encodeURIComponent(portee)}` : ''}`),

  /** Le Calendrier d'un mois (« 2026-10 »), jour par jour, avec l'heure (ticket 15) ; une portée, comme l'Agenda. */
  calendrier: (mois: string | null, portee?: string) => {
    const requete = new URLSearchParams();
    if (mois) requete.set('mois', mois);
    if (portee) requete.set('projet', portee);
    const texte = requete.toString();
    return appeler<MoisDuCalendrier>(`/api/calendrier${texte ? `?${texte}` : ''}`);
  },

  /** Préparer un jour : ce que le monde impose, la journée déjà là, ce qui sera possible. */
  preparation: (jour: string, ou?: Ou, courage?: CourageDeMaintenant) => {
    // Le courage d'une journée (chantier `telephone-journee-agenda`, ticket 01) : seulement
    // quand la question l'a demandé ; sans lui, le serveur garde celui de la journée, sinon Normal.
    const requete = new URLSearchParams();
    if (ou) requete.set('ou', ou);
    if (courage) requete.set('courage', courage);
    const q = requete.toString();
    return appeler<PreparationDuJour>(`/api/journees/${jour}${q ? `?${q}` : ''}`);
  },

  /**
   * Les heures qu'aurait la journée, sans rien écrire. Une tâche fixée part avec son heure
   * (`{ id, heure }`) ; une heure fixée qui en chevauche une autre arrive en `EchecApi`
   * `heure_fixee_chevauche`, ses `details` disent laquelle et avec quoi.
   */
  rangerLeJour: (jour: string, choix: { debut: string; taches: TacheEnvoyee[] }) =>
    appeler<ReponseRangement>(`/api/journees/${jour}/ranger`, {
      method: 'POST',
      body: JSON.stringify(choix),
    }),

  /**
   * « Propose-moi une journée » (chantier `telephone-journee-agenda`, ticket 02) : une journée qui
   * remplit le temps sans le dépasser, le débloquant en tête, à ses heures. Rien n'est écrit.
   */
  proposerUnJour: (jour: string, reglages: ReglagesDeProposition) =>
    appeler<PropositionDeJournee>(`/api/journees/${jour}/proposer`, {
      method: 'POST',
      body: JSON.stringify(reglages),
    }),

  /**
   * ↻ : la première tâche qui tient à la place de `remplacer`, hors de la proposition et de
   * `ecartees`. Rien qui tienne : `EchecApi` `rien_qui_tienne`, la tâche reste. Rien n'est écrit.
   */
  remplacerDansLaProposition: (
    jour: string,
    demande: ReglagesDeProposition & { taches: string[]; remplacer: string; ecartees: string[] }
  ) =>
    appeler<ReponseRemplacement>(`/api/journees/${jour}/proposer/remplacer`, {
      method: 'POST',
      body: JSON.stringify(demande),
    }),

  /**
   * « C'est ma journée du N » : la journée, gardée à part des bulles. `version` : celle de la
   * journée lue avant de la composer, `null` pour un jour lu vide ; changée depuis sur un autre
   * appareil, `EchecApi` `journee_changee`, rien n'est écrit (chantier nettoyage-regles, ticket 04).
   */
  enregistrerLeJour: (
    jour: string,
    journee: {
      debut: string;
      tempsMin: number;
      ou: Ou;
      courage?: CourageDeMaintenant;
      taches: TacheEnvoyee[];
      version?: string | null;
    }
  ) =>
    appeler<ReponseJourneeEcrite>(`/api/journees/${jour}`, {
      method: 'PUT',
      body: JSON.stringify(journee),
    }),

  /**
   * Le + : une tâche de plus dans la journée d'un jour, à la suite, ajoutée par le serveur
   * (chantier nettoyage-gestes, ticket 03). Refusée comme « C'est ma journée » la refuserait.
   */
  ajouterAUnJour: (jour: string, tache: string) =>
    appeler<ReponseJourneeEcrite>(`/api/journees/${jour}/taches`, {
      method: 'POST',
      body: JSON.stringify({ tache }),
    }),

  /** Défaire une journée préparée : ses tâches redeviennent libres. */
  defaireLeJour: (jour: string) => appeler<void>(`/api/journees/${jour}`, { method: 'DELETE' }),

  /** « Maintenant » : la journée du jour, et les bandeaux de la veille et du lendemain (ticket 16). */
  journeeDuJour: () => appeler<JourneeDuJour>('/api/journee-du-jour'),

  /**
   * « Garder pour aujourd'hui » (chantier `flottaison-suite`, ticket 03) : la tâche entre dans la
   * journée d'aujourd'hui, à la suite. Un refus (`pas_faisable_aujourdhui`, `ne_tient_pas`)
   * arrive en `EchecApi` dont le code est le refus.
   */
  garderPourAujourdhui: (tache: string) =>
    appeler<ReponseJourneeEcrite>('/api/journee-du-jour/taches', {
      method: 'POST',
      body: JSON.stringify({ tache }),
    }),

  /**
   * « Plus tard aujourd'hui » : la tâche passe en fin de la journée du jour, à la suite ; le
   * serveur refait les heures (chantier nettoyage-gestes, ticket 03).
   */
  plusTardAujourdhui: (tache: string) =>
    appeler<ReponseJourneeEcrite>('/api/journee-du-jour/plus-tard', {
      method: 'POST',
      body: JSON.stringify({ tache }),
    }),

  /** Répondre au bandeau de la veille ou du lendemain d'une journée. */
  repondreAuBandeau: (jour: string, reponse: ReponseAuBandeau) =>
    appeler<ReponseDuBandeau>(`/api/journees/${jour}/reponse`, {
      method: 'POST',
      body: JSON.stringify(reponse),
    }),

  /** Le modele : ce qu'il apporte, et surtout ce qu'il ne donnera jamais. */
  templates: () => appeler<ReponseTemplates>('/api/templates'),

  chargerTemplate: (cle: string) =>
    appeler<ReponseChargement>(`/api/templates/${cle}/charger`, { method: 'POST' }),

  /** L'invite à coller dans son IA, tirée de templates/schema-plan.md (ticket 24). */
  inviteDuPlan: () => appeler<ReponseInviteDuPlan>('/api/plans/invite'),

  /**
   * Importer le texte collé depuis son IA. Un plan invalide revient en refus
   * `plan_invalide`, avec `details.erreurs` : ce qu'il faut corriger, bulle par bulle.
   */
  importerPlan: (texte: string) =>
    appeler<ReponseImportDuPlan>('/api/plans/importer', {
      method: 'POST',
      body: JSON.stringify({ texte }),
    }),

  /**
   * Le tuto et l'Aide (tickets 31, 33, et 11 de retours-testeurs) : dire où l'on est, et
   * recevoir l'étape à montrer (une bulle de la visite ou le bandeau du bas) avec la liste de
   * l'Aide. Le serveur lance la présentation si elle doit se lancer et fait avancer la partie
   * en cours d'après ce qui est fait ; `tuto: null`, rien à montrer.
   */
  tuto: (ecran: EcranDuTuto | null) =>
    appeler<ReponseDuTuto>('/api/tuto', {
      method: 'POST',
      body: JSON.stringify({ ecran }),
    }),

  /**
   * Un bouton du tuto, ou une partie lancée depuis l'Aide (`lancer`, avec sa clé). `etape` :
   * celle que le tuto montrait.
   */
  gesteDuTuto: (
    geste: GesteDuTuto,
    etape: string | null,
    ecran: EcranDuTuto | null,
    partie: string | null = null,
    /** Un niveau entier de l'Aide, lancé par son titre (ticket 17 de retours-testeurs). */
    niveau: number | null = null
  ) =>
    appeler<ReponseDuTuto>('/api/tuto/geste', {
      method: 'POST',
      body: JSON.stringify({ geste, etape, partie, niveau, ecran }),
    }),

  /** L'export porte une SELECTION, pas un rendez-vous isole. */
  urlExport: (ids: string[]) => `/api/export.ics?bulles=${ids.join(',')}`,

  /**
   * Créer une bulle. Un titre, un parent : ajouter une bulle dans une autre, c'est déjà poser
   * une condition (SPEC §3). Un projet (sans parent) peut recevoir sa couleur, l'une des cinq
   * (`telephone-serveur` 01, le + du téléphone) ; sans elle, le tourniquet. Ce que la bulle est
   * (une idée, une échéance, un rendez-vous) part dans la même demande, tout ou rien (chantier
   * nettoyage-fiche, ticket 03) : un refus ne laisse aucune bulle à moitié réglée.
   */
  creer: (titre: string, parentId: string | null, champs: ChampsALaNaissance = {}) => {
    const envoi: EnvoiDeCreation = { ...champs, titre, parentId };
    return appeler<ReponseCreation>('/api/noeuds', { method: 'POST', body: JSON.stringify(envoi) });
  },

  modifier: (id: string, champs: Partial<Bulle>) =>
    appeler<ReponseBulle>(`/api/noeuds/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(champs),
    }),

  /** La couleur d'un projet et son habit, depuis le panneau « Habit » (chantier habit-du-projet, ticket 02). */
  habiller: (id: string, changement: ChangementDHabit) =>
    appeler<ReponseBulle>(`/api/noeuds/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(changement),
    }),

  /** La couleur, la forme ou le trait d'une bulle, depuis son habit (chantier habit-des-bulles, 04). */
  habillerLaBulle: (id: string, changement: ChangementDHabitDUneBulle) =>
    appeler<ReponseBulle>(`/api/noeuds/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(changement),
    }),

  /** Le mode d'une bulle, projet compris, depuis le panneau « Mode » (chantier modes-de-la-carte). */
  choisirLeMode: (id: string, changement: ChangementDeMode) =>
    appeler<ReponseBulle>(`/api/noeuds/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(changement),
    }),

  /** Le type et le papier de la fiche d'une bulle, depuis le menu « Type ▾ » (chantier types-de-fiche, 02). */
  choisirLaFiche: (id: string, changement: ChangementDeFiche) =>
    appeler<ReponseBulle>(`/api/noeuds/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(changement),
    }),

  /**
   * Valider une bulle, et recevoir l'inventaire de ce qui restait dedans.
   * L'inventaire vient APRÈS : on ne demande pas la permission, on montre ce que le geste
   * laisse derrière (SPEC §3).
   */
  valider: (id: string) =>
    appeler<Inventaire>(`/api/noeuds/${id}/valider`, { method: 'POST' }),

  /**
   * Défaire une validation : la bulle retrouve l'état que `valider` a renvoyé. Refusé
   * (`plus_acquise`) si elle n'est plus acquise, parce qu'autre chose l'a changée depuis.
   */
  annulerValidation: (id: string, etatAvant: Etat) =>
    appeler<ReponseBulle>(`/api/noeuds/${id}/annuler-validation`, {
      method: 'POST',
      body: JSON.stringify({ etatAvant }),
    }),

  /** Déplacer une bulle avec tout ce qu'elle contient. `null` la sort à la racine. */
  deplacer: (id: string, parentId: string | null) =>
    appeler<ReponseBulle>(`/api/noeuds/${id}/deplacer`, {
      method: 'POST',
      body: JSON.stringify({ parentId }),
    }),

  supprimer: (id: string) => appeler<void>(`/api/noeuds/${id}`, { method: 'DELETE' }),

  /** Combien de bulles partiraient avec elle : tout son sous-arbre, elle exclue (ticket 10). */
  emportees: (id: string) => appeler<ReponseEmportees>(`/api/noeuds/${id}/emportees`),

  /**
   * Hierarchiser : ton ordre, pose sur plusieurs bulles d'un coup.
   * En un seul appel : c'est un geste unique, et le voir arriver en morceaux ferait
   * clignoter la feuille pendant que les bulles rejoignent leur place.
   */
  poserRangs: (rangs: { id: string; rang: number | null }[]) =>
    appeler<ReponseRangs>('/api/noeuds/rangs', {
      method: 'POST',
      body: JSON.stringify({ rangs }),
    }),

  /** La recherche traverse TOUS les dossiers : c'est a ca que sert une transversale. */
  rechercher: (q: string) =>
    appeler<ReponseRecherche>(`/api/recherche?q=${encodeURIComponent(q)}`),

  liens: (id: string) => appeler<Liens>(`/api/dependances/${id}`),

  /**
   * Poser « ca attend… ». Peut echouer sur un cycle — et ce refus apprend quelque chose. Avec une
   * sorte (« Lier » sur la carte, chantier liens-entre-bulles, ticket 04) : un « sert à » ou un
   * « touche à » ; absente, un « attend ».
   */
  lier: (sourceId: string, cibleId: string, sorte?: SorteDeLien) =>
    appeler<ReponseLien>('/api/dependances', {
      method: 'POST',
      body: JSON.stringify({ sourceId, cibleId, sorte }),
    }),

  delier: (lienId: string) => appeler<void>(`/api/dependances/${lienId}`, { method: 'DELETE' }),

  /** Cacher ou montrer un lien (chantier liens-entre-bulles, ticket 03 : l'œil d'un trait). */
  montrerLeLien: (lienId: string, visible: boolean) =>
    appeler<ReponseLienChange>(`/api/dependances/${lienId}`, { method: 'PATCH', body: JSON.stringify({ visible }) }),

  /** Inverser un « attend » ou un « sert à » (⇄). Un « attend » qui bouclerait : refus `cycle`. */
  inverserLeLien: (lienId: string) =>
    appeler<ReponseLienChange>(`/api/dependances/${lienId}/inverser`, { method: 'POST' }),

  /**
   * Inserer un maillon, depuis le refus d'une boucle : une nouvelle bulle nait dans la bulle
   * bloquee par ce lien, et remplace le lien.
   */
  inserer: (lienId: string, titre: string) =>
    appeler<ReponseInsertion>(`/api/dependances/${lienId}/inserer`, {
      method: 'POST',
      body: JSON.stringify({ titre }),
    }),

  /**
   * Les papiers posés sur la carte d'une bulle, post-it et listes libres (chantier
   * post-it-et-listes, ticket 02). Jamais lus par le moteur : ni la feuille, ni Maintenant ne
   * les portent, ils ont leurs routes à eux.
   */
  papiers: (noeudId: string) => appeler<ReponsePapiers>(`/api/noeuds/${noeudId}/papiers`),

  /** Poser un papier : sans place, le serveur en propose une libre près du centre. */
  poserUnPapier: (noeudId: string, envoi: EnvoiDUnPapier) =>
    appeler<ReponsePapier>(`/api/noeuds/${noeudId}/papiers`, {
      method: 'POST',
      body: JSON.stringify(envoi),
    }),

  /** Son titre, son texte, son papier, sa place, sa taille : ce qui manque ne change pas. */
  modifierUnPapier: (id: string, changement: ChangementDePapier) =>
    appeler<ReponsePapier>(`/api/papiers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(changement),
    }),

  supprimerUnPapier: (id: string) => appeler<void>(`/api/papiers/${id}`, { method: 'DELETE' }),

  /** Les lignes d'une liste libre (ticket 04) : chaque geste rend la liste entière. */
  ajouterUneLigne: (papierId: string, envoi: EnvoiDUneLigne) =>
    appeler<ReponsePapier>(`/api/papiers/${papierId}/lignes`, {
      method: 'POST',
      body: JSON.stringify(envoi),
    }),

  modifierUneLigne: (papierId: string, ligneId: string, changement: ChangementDeLigne) =>
    appeler<ReponsePapier>(`/api/papiers/${papierId}/lignes/${ligneId}`, {
      method: 'PATCH',
      body: JSON.stringify(changement),
    }),

  retirerUneLigne: (papierId: string, ligneId: string) =>
    appeler<ReponsePapier>(`/api/papiers/${papierId}/lignes/${ligneId}`, { method: 'DELETE' }),

  /**
   * Le profil du compte (chantier profil, tickets 01 et 02) : ses champs, ses lignes libres, les
   * propositions de Claude à part, ses contacts relus dans les bulles. Rien ne calcule avec : ni le
   * moteur, ni Maintenant.
   */
  profil: () => appeler<ReponseProfil>('/api/profil'),

  /** Remplace le profil entier : tous les champs partent à chaque correction, un champ absent s'efface. */
  ecrireLeProfil: (profil: ChampsDuProfil) =>
    appeler<ReponseChampsDuProfil>('/api/profil', {
      method: 'PUT',
      body: JSON.stringify(profil),
    }),

  /** Le premier formulaire vu (ticket 03) : il ne s'ouvre plus seul. La première date seule est gardée. */
  noterLeFormulaireVu: () => appeler<ReponseFormulaireVu>('/api/profil/formulaire-vu', { method: 'POST' }),

  /** Une ligne libre neuve, à la fin de sa feuille. */
  ajouterUneLigneDeProfil: (envoi: EnvoiDUneLigneDeProfil) =>
    appeler<ReponseLigneDeProfil>('/api/profil/lignes', {
      method: 'POST',
      body: JSON.stringify(envoi),
    }),

  /** Son intitulé, sa valeur : ce qui manque ne change pas ; sa feuille reste. */
  modifierUneLigneDeProfil: (id: string, changement: ChangementDeLigneDeProfil) =>
    appeler<ReponseLigneDeProfil>(`/api/profil/lignes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(changement),
    }),

  /** Retirer une ligne ; une ligne proposée retirée, c'est Non (chantier profil-pour-claude, ticket 02). */
  retirerUneLigneDeProfil: (id: string) => appeler<void>(`/api/profil/lignes/${id}`, { method: 'DELETE' }),

  /**
   * Garder une ligne proposée par Claude (chantier profil-pour-claude, ticket 02) : elle entre à la
   * fin de sa feuille, son origine gardée. Plus proposée : refus `pas_une_proposition`.
   */
  garderUneProposition: (id: string) =>
    appeler<ReponseLigneDeProfil>(`/api/profil/propositions/${id}/garder`, { method: 'POST' }),

  /*
   * La boîte d'entrée (chantier boite-d-entree, ticket 02) : ce qu'on note vite, pas encore rangé.
   * Refus : `boite_pleine` (422), `note_introuvable` (404), `parent_introuvable` (404).
   */

  /** Les notes, la plus récente d'abord. */
  boite: () => appeler<ReponseBoite>('/api/boite'),

  noterDansLaBoite: (texte: string) =>
    appeler<ReponseNote>('/api/boite', { method: 'POST', body: JSON.stringify({ texte }) }),

  retirerUneNote: (id: string) => appeler<void>(`/api/boite/${id}`, { method: 'DELETE' }),

  /** La note devient une bulle à la fin des enfants de la mère, et quitte la boîte. */
  rangerUneNote: (id: string, mereId: string) =>
    appeler<ReponseCreation>(`/api/boite/${id}/ranger`, { method: 'POST', body: JSON.stringify({ mereId }) }),

  /*
   * Les notifications au téléphone (chantier notifications-socle, ticket 03), par la session du
   * navigateur. `adresse` : l'adresse d'envoi que le navigateur a donnée à cet appareil ; une
   * adresse que le compte ne connaît pas arrive en `EchecApi` `abonnement_inconnu` (404).
   */

  /** La clé publique du Web Push, que le navigateur demande pour s'abonner. */
  cleDesNotifications: () => appeler<ReponseCleDesNotifications>('/api/notifications/cle'),

  abonnerCetAppareil: (envoi: EnvoiDAbonnement) =>
    appeler<ReponseAbonnement>('/api/notifications/abonnement', { method: 'POST', body: JSON.stringify(envoi) }),

  desabonnerCetAppareil: (adresse: string) =>
    appeler<ReponseDesabonnement>('/api/notifications/desabonnement', {
      method: 'POST',
      body: JSON.stringify({ adresse }),
    }),

  /** « Cet appareil est vu » : son fuseau et sa dernière vue, à chaque ouverture de l'app. */
  appareilVu: (adresse: string, fuseau: string) =>
    appeler<ReponseAppareilVu>('/api/notifications/vu', { method: 'POST', body: JSON.stringify({ adresse, fuseau }) }),

  /** Les réglages du compte, pour tous ses appareils, lisibles même sans abonnement. */
  reglagesDesNotifications: () => appeler<ReglagesDesNotifications>('/api/notifications/reglages'),

  ecrireReglagesDesNotifications: (reglages: ReglagesDesNotifications) =>
    appeler<ReglagesDesNotifications>('/api/notifications/reglages', { method: 'PUT', body: JSON.stringify(reglages) }),

  /**
   * Une lecture de l'app pour le journal des gestes (chantier journal-des-gestes, ticket 05) :
   * ouvrir ou fermer un projet ou une fiche, changer d'angle. L'écran n'attend rien : rien n'est
   * rendu, un échec se tait. En `keepalive`, pour qu'une fermeture parte même quand l'onglet se
   * ferme. L'angle joint est celui du signal (celui d'avant pour une fermeture).
   */
  signalerUneLecture: ({ geste, noeudId, avant, apres, angle }: SignalDeLecture): void => {
    appeler<void>('/api/journal/lecture', {
      method: 'POST',
      keepalive: true,
      headers: { [EN_TETES_DU_CONTEXTE.angle]: angle },
      body: JSON.stringify({ geste, noeudId, ...(avant ? { avant } : {}), ...(apres ? { apres } : {}) }),
    }).catch(() => undefined);
  },

  /** Une notification d'essai vers cet appareil, et lui seul. */
  essaiDeNotification: (adresse: string) =>
    appeler<ReponseEssai>('/api/notifications/essai', { method: 'POST', body: JSON.stringify({ adresse }) }),
};
